import { Stack, Typography } from "@mui/material";
import { FC, Fragment, ReactElement } from "react";
import { Event, Maybe } from "../../graphql/types";
import { HumanDate } from "../Date";
import { DetailContainerRow, DownloadDetail, DownloadsForFlow, DownloadsForUpload } from "../common/FileDetailViews";
import IconCopy from "../common/IconCopy";
import { InlineBlock, Spacer } from "../ui/common";
import { ToggleDetailIcon } from "../ui/icons";
import {
  EventLogNarrowRowStyled,
  EventLogRowStyled,
  EventLogStatusIcon,
  EventMessageCell,
  FixedWidthCell,
} from "./styles";

interface EventLogRowProps {
  event: Event;
  onClick?: (click: React.MouseEvent<HTMLAnchorElement, MouseEvent>, eventId: string) => void;
  selected?: boolean;
}
const MAX_SHORT_MESSAGE_LENGTH = 200;

const parseMessage = (message: string | null | undefined) => {
  const longMessage = message || "";
  const suffix = longMessage.length > MAX_SHORT_MESSAGE_LENGTH ? " ..." : "";
  const shortMessage = `${longMessage.substring(0, MAX_SHORT_MESSAGE_LENGTH)}${suffix}`;
  return { longMessage, shortMessage };
};

const EventMessage = ({
  file_path,
  message,
  retry_count,
  retry_limit,
}: {
  file_path?: Maybe<string>;
  message?: Maybe<string>;
  retry_count?: Maybe<number>;
  retry_limit?: Maybe<number>;
}) => {
  let retriesText = "";
  if (retry_count && retry_limit) {
    retriesText = `Retry count: ${retry_count}/${retry_limit - 1}`;
  }
  return (
    <Typography variant="body.small.regular" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
      {file_path} - {message} {retriesText}
    </Typography>
  );
};

const EventLogRow: FC<EventLogRowProps> = ({ event, onClick, selected }) => {
  const { longMessage, shortMessage } = parseMessage(event.message || "");

  let detailRow: ReactElement | null = null;
  if (selected) {
    let detailRowComponent: ReactElement | null = null;
    if (event.upload) {
      detailRowComponent = <DownloadsForUpload upload_id={event.upload.id} showUploads={true} />;
    } else if (event.download) {
      detailRowComponent = <DownloadDetail download_id={event.download.id} />;
    } else if (event.flow) {
      detailRowComponent = <DownloadsForFlow flow_id={event.flow.id} />;
    }
    detailRow = (
      <DetailContainerRow key={`detail:${event.id}`}>
        <td colSpan={5}>{detailRowComponent}</td>
      </DetailContainerRow>
    );
  }

  return (
    <Fragment>
      <EventLogRowStyled key={event.id} className={selected ? "selected" : ""}>
        <FixedWidthCell size={"20px"}>
          <ToggleDetailIcon selected={selected} onClick={onClick && ((click) => onClick(click, event.id))} />
        </FixedWidthCell>
        <FixedWidthCell size={"130px"} center={true}>
          <EventLogStatusIcon severity={event.severity} willRetry={event.will_retry} />
        </FixedWidthCell>
        <FixedWidthCell size={"180px"}>
          <Stack direction="row" sx={{ alignItems: "center" }}>
            <InlineBlock>
              <HumanDate placement="right" date={event.created} />
            </InlineBlock>
            <IconCopy tooltip={`Event ID: ${event.id}`} clipboard={`Event ID: ${event.id}`} />
          </Stack>
        </FixedWidthCell>
        <EventMessageCell>
          <EventMessage
            file_path={event.project_name}
            message={selected ? longMessage : shortMessage}
            retry_count={event.retry_count}
            retry_limit={event.retry_limit}
          />
          <IconCopy tooltip={`${longMessage}`} clipboard={longMessage} />
        </EventMessageCell>
      </EventLogRowStyled>
      {detailRow}
    </Fragment>
  );
};

const EventLogNarrowRow: FC<EventLogRowProps> = ({ event }) => {
  const results = parseMessage(event.message || "");
  return (
    <EventLogNarrowRowStyled>
      <Spacer>
        <EventLogStatusIcon severity={event.severity} willRetry={event.will_retry} />
      </Spacer>
      <Spacer>
        <HumanDate placement="right" date={event.created} />
      </Spacer>
      {results.shortMessage}
    </EventLogNarrowRowStyled>
  );
};

export { EventLogNarrowRow, EventLogRow, EventMessage, parseMessage };
